import { delay } from './utils';
import { getItem, setItem, removeItem, isBrowser } from './safeStorage';
import type {
  User,
  UserRole,
  Shop,
  Category,
  Product,
  Cart,
  Order,
  OrderItem,
  QPayPayment,
  Refund,
  RefundType,
  Banner,
  OrderStatus,
  StatusHistoryEntry,
} from '@/types';

// Storage keys
const STORAGE_KEYS = {
  USER: 'nomadshop_user',
  ROLE: 'nomadshop_role',
  SHOPS: 'nomadshop_shops',
  CATEGORIES: 'nomadshop_categories',
  PRODUCTS: 'nomadshop_products',
  CART: 'nomadshop_cart',
  ORDERS: 'nomadshop_orders',
  WISHLIST: 'nomadshop_wishlist',
  QPAY_PAYMENTS: 'nomadshop_qpay_payments',
  REFUNDS: 'nomadshop_refunds',
  BANNERS: 'nomadshop_banners',
  FEATURED_CATEGORIES: 'nomadshop_featured_categories',
  SELLER_DEMO_META: 'nomadshop_seller_demo_meta',
};

// Helper functions
function safeJsonParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function getStorage<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue;
  
  const raw = getItem(key);
  if (!raw) return defaultValue;
  
  // Try to parse, catch any JSON errors
  let parsed: T;
  try {
    parsed = JSON.parse(raw) as T;
  } catch {
    // Self-heal: corrupted JSON detected, overwrite with safe default
    setItem(key, JSON.stringify(defaultValue));
    return defaultValue;
  }
  
  return parsed;
}

function setStorage<T>(key: string, value: T): void {
  setItem(key, JSON.stringify(value));
}

// Mock data generators
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

// Seed data
function seedData() {
  if (!isBrowser()) return;
  
  // Check if already seeded
  if (getItem('nomadshop_seeded')) return;
  
  const shops: Shop[] = [
    { id: 'shop1', ownerUserId: 'seller1', name: 'Mongolian Crafts' },
    { id: 'shop2', ownerUserId: 'seller2', name: 'Nomad Essentials' },
    { id: 'shop3', ownerUserId: 'seller3', name: 'Ulaanbaatar Market' },
  ];
  
  // Root categories
  const rootElectronics = { id: 'cat_electronics', name: 'Электроника (전자제품)', sort: 1 };
  const rootBeauty = { id: 'cat_beauty', name: 'Бьюти (뷰티)', sort: 2 };
  const rootHome = { id: 'cat_home', name: 'Бытовая техника / товары для дома (생활가전)', sort: 3 };

  const categories: Category[] = [
    // Root categories
    rootElectronics,
    rootBeauty,
    rootHome,
    
    // Electronics children
    { id: 'cat_e1', name: '휴대폰 — мобильные телефоны', sort: 1, parentId: rootElectronics.id },
    { id: 'cat_e2', name: '태블릿 — планшеты', sort: 2, parentId: rootElectronics.id },
    { id: 'cat_e3', name: '컴퓨터, 노트북 — компьютеры и ноутбуки', sort: 3, parentId: rootElectronics.id },
    { id: 'cat_e4', name: '스마트워치 — умные часы', sort: 4, parentId: rootElectronics.id },
    { id: 'cat_e5', name: '이어폰, 스피커 — наушники и колонки', sort: 5, parentId: rootElectronics.id },
    { id: 'cat_e6', name: '카메라 — камеры', sort: 6, parentId: rootElectronics.id },
    { id: 'cat_e7', name: '비디오 게임 — видеоигры / приставки', sort: 7, parentId: rootElectronics.id },
    { id: 'cat_e8', name: '프로젝터 — проекторы', sort: 8, parentId: rootElectronics.id },
    { id: 'cat_e9', name: '액세서리 — аксессуары', sort: 9, parentId: rootElectronics.id },
    
    // Beauty children
    { id: 'cat_b1', name: '스킨케어 — уход за кожей', sort: 1, parentId: rootBeauty.id },
    { id: 'cat_b2', name: '메이크업 — макияж', sort: 2, parentId: rootBeauty.id },
    { id: 'cat_b3', name: '헤어케어 — уход за волосами', sort: 3, parentId: rootBeauty.id },
    { id: 'cat_b4', name: '바디케어 — уход за телом', sort: 4, parentId: rootBeauty.id },
    { id: 'cat_b5', name: '남성 뷰티 — мужская косметика', sort: 5, parentId: rootBeauty.id },
    { id: 'cat_b6', name: '손, 네일케어 — уход за руками и ногтями', sort: 6, parentId: rootBeauty.id },
    { id: 'cat_b7', name: '향수 — парфюм', sort: 7, parentId: rootBeauty.id },
    { id: 'cat_b8', name: '건강식품 — БАДы / товары для здоровья', sort: 8, parentId: rootBeauty.id },
    { id: 'cat_b9', name: '디지털 기기 — цифровые устройства', sort: 9, parentId: rootBeauty.id },
    { id: 'cat_b10', name: '메이크업툴 — инструменты для макияжа', sort: 10, parentId: rootBeauty.id },
    
    // Home children (Kitchen)
    { id: 'cat_h1', name: '식기세척건조기 — посудомоечные / сушилки', sort: 1, parentId: rootHome.id },
    { id: 'cat_h2', name: '에어프라이어/튀김기 — аэрофритюрницы / фритюрницы', sort: 2, parentId: rootHome.id },
    { id: 'cat_h3', name: '전기밥솥 — рисоварки', sort: 3, parentId: rootHome.id },
    { id: 'cat_h4', name: '믹서기 — блендеры / миксеры', sort: 4, parentId: rootHome.id },
    { id: 'cat_h5', name: '토스터기 — тостеры', sort: 5, parentId: rootHome.id },
    { id: 'cat_h6', name: '원액기 — соковыжималки (шнековые)', sort: 6, parentId: rootHome.id },
    { id: 'cat_h7', name: '간식메이커 — приборы для снеков', sort: 7, parentId: rootHome.id },
    { id: 'cat_h8', name: '커피메이커 — кофеварки / кофемашины', sort: 8, parentId: rootHome.id },
    { id: 'cat_h9', name: '전기포트 — электрочайники', sort: 9, parentId: rootHome.id },
    { id: 'cat_h10', name: '전기그릴/팬 — электрогрили / электро-сковороды', sort: 10, parentId: rootHome.id },
    { id: 'cat_h11', name: '전기쿠커 — электрокастрюли / мультиварки', sort: 11, parentId: rootHome.id },
    { id: 'cat_h12', name: '주방보조기기 — кухонные помощники', sort: 12, parentId: rootHome.id },
    { id: 'cat_h13', name: '계량측정기기 — измерительные приборы (весы и т.п.)', sort: 13, parentId: rootHome.id },
    { id: 'cat_h14', name: '음식물처리기 — утилизаторы/измельчители пищевых отходов', sort: 14, parentId: rootHome.id },
    { id: 'cat_h15', name: '다지기 — чопперы / измельчители', sort: 15, parentId: rootHome.id },
    
    // Home children (Household)
    { id: 'cat_h16', name: '청소기 — пылесосы', sort: 16, parentId: rootHome.id },
    { id: 'cat_h17', name: '공기청정기 — очистители воздуха', sort: 17, parentId: rootHome.id },
    { id: 'cat_h18', name: '안마기기 — массажёры', sort: 18, parentId: rootHome.id },
    { id: 'cat_h19', name: '다리미 — утюги', sort: 19, parentId: rootHome.id },
    { id: 'cat_h20', name: '보풀제거기, 재봉틀 — машинки от катышков, швейные машины', sort: 20, parentId: rootHome.id },
    { id: 'cat_h21', name: '면도기, 제모기 — электробритвы, эпиляторы', sort: 21, parentId: rootHome.id },
    { id: 'cat_h22', name: '드라이기, 고데기 — фены, стайлеры/плойки/утюжки', sort: 22, parentId: rootHome.id },
    { id: 'cat_h23', name: '사무가전 — офисная техника', sort: 23, parentId: rootHome.id },
    { id: 'cat_h24', name: '전기용품 — электропринадлежности', sort: 24, parentId: rootHome.id },
    { id: 'cat_h25', name: '난방매트 — подогреваемые коврики/матрасы', sort: 25, parentId: rootHome.id },
    { id: 'cat_h26', name: '난방기기 — обогреватели', sort: 26, parentId: rootHome.id },
    { id: 'cat_h27', name: '냉방기기 — устройства охлаждения (вентиляторы/кондиционеры и т.п.)', sort: 27, parentId: rootHome.id },
    { id: 'cat_h28', name: '해충퇴치기 — отпугиватели насекомых', sort: 28, parentId: rootHome.id },
    { id: 'cat_h29', name: '가습기 — увлажнители воздуха', sort: 29, parentId: rootHome.id },
  ];
  
  // Get leaf category IDs for product distribution
  const leafCategories = categories.filter(c => c.parentId).map(c => c.id);
  
  const products: Product[] = [
    // Electronics products (distributed across leaf categories)
    { id: 'p1', shopId: 'shop1', categoryId: 'cat_e1', title: 'Smartphone Pro', description: 'Latest smartphone with great features', price: 850000, salePrice: 750000, images: [{ url: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p2', shopId: 'shop2', categoryId: 'cat_e1', title: 'Mobile Phone X', description: 'High-performance mobile phone', price: 650000, images: [{ url: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p3', shopId: 'shop3', categoryId: 'cat_e2', title: 'Tablet Air', description: 'Premium tablet device', price: 450000, salePrice: 380000, images: [{ url: 'https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p4', shopId: 'shop1', categoryId: 'cat_e3', title: 'Laptop Ultra', description: 'High-end laptop computer', price: 1200000, images: [{ url: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p5', shopId: 'shop2', categoryId: 'cat_e3', title: 'Notebook Pro', description: 'Professional notebook', price: 950000, salePrice: 850000, images: [{ url: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p6', shopId: 'shop3', categoryId: 'cat_e4', title: 'Smart Watch', description: 'Fitness tracking smartwatch', price: 350000, images: [{ url: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p7', shopId: 'shop1', categoryId: 'cat_e5', title: 'Wireless Earbuds', description: 'High-quality wireless earbuds', price: 120000, images: [{ url: 'https://images.unsplash.com/photo-1590658268037-6bf12165a8df?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p8', shopId: 'shop2', categoryId: 'cat_e5', title: 'Bluetooth Speaker', description: 'Portable speaker system', price: 85000, salePrice: 65000, images: [{ url: 'https://images.unsplash.com/photo-1608043152269-423dbba4e7e1?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p9', shopId: 'shop3', categoryId: 'cat_e6', title: 'Digital Camera', description: 'Professional camera', price: 550000, images: [{ url: 'https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p10', shopId: 'shop1', categoryId: 'cat_e7', title: 'Gaming Console', description: 'Latest gaming console', price: 750000, salePrice: 680000, images: [{ url: 'https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?w=400', sort: 1, isMain: true }], isActive: true },
    
    // Beauty products
    { id: 'p11', shopId: 'shop2', categoryId: 'cat_b1', title: 'Skincare Set', description: 'Complete skincare routine', price: 125000, salePrice: 99000, images: [{ url: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p12', shopId: 'shop3', categoryId: 'cat_b1', title: 'Face Serum', description: 'Anti-aging face serum', price: 85000, images: [{ url: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p13', shopId: 'shop1', categoryId: 'cat_b2', title: 'Makeup Palette', description: 'Professional makeup set', price: 95000, images: [{ url: 'https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p14', shopId: 'shop2', categoryId: 'cat_b2', title: 'Lipstick Set', description: 'Premium lipstick collection', price: 55000, salePrice: 45000, images: [{ url: 'https://images.unsplash.com/photo-1586495777744-4413f21062fa?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p15', shopId: 'shop3', categoryId: 'cat_b3', title: 'Hair Care Kit', description: 'Professional hair care products', price: 75000, images: [{ url: 'https://images.unsplash.com/photo-1522338242992-e1a55dcb0b8f?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p16', shopId: 'shop1', categoryId: 'cat_b4', title: 'Body Lotion', description: 'Moisturizing body lotion', price: 45000, images: [{ url: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p17', shopId: 'shop2', categoryId: 'cat_b7', title: 'Perfume', description: 'Luxury fragrance', price: 180000, images: [{ url: 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p18', shopId: 'shop3', categoryId: 'cat_b8', title: 'Vitamin Supplements', description: 'Health supplements set', price: 65000, salePrice: 55000, images: [{ url: 'https://images.unsplash.com/photo-1550572017-edd951b55104?w=400', sort: 1, isMain: true }], isActive: true },
    
    // Home products
    { id: 'p19', shopId: 'shop1', categoryId: 'cat_h1', title: 'Dishwasher', description: 'Energy-efficient dishwasher', price: 850000, salePrice: 750000, images: [{ url: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p20', shopId: 'shop2', categoryId: 'cat_h2', title: 'Air Fryer', description: 'Multi-function air fryer', price: 280000, images: [{ url: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p21', shopId: 'shop3', categoryId: 'cat_h3', title: 'Rice Cooker', description: 'Smart rice cooker', price: 180000, salePrice: 150000, images: [{ url: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p22', shopId: 'shop1', categoryId: 'cat_h4', title: 'Blender Pro', description: 'High-speed blender', price: 145000, images: [{ url: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p23', shopId: 'shop2', categoryId: 'cat_h8', title: 'Coffee Maker', description: 'Automatic coffee machine', price: 320000, images: [{ url: 'https://images.unsplash.com/photo-1517487881594-2787fef5ebf7?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p24', shopId: 'shop3', categoryId: 'cat_h9', title: 'Electric Kettle', description: 'Fast-boil electric kettle', price: 65000, salePrice: 55000, images: [{ url: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p25', shopId: 'shop1', categoryId: 'cat_h11', title: 'Multi-Cooker', description: 'Versatile multi-cooker', price: 380000, images: [{ url: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p26', shopId: 'shop2', categoryId: 'cat_h16', title: 'Vacuum Cleaner', description: 'Powerful vacuum cleaner', price: 450000, salePrice: 380000, images: [{ url: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p27', shopId: 'shop3', categoryId: 'cat_h17', title: 'Air Purifier', description: 'HEPA air purifier', price: 550000, images: [{ url: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p28', shopId: 'shop1', categoryId: 'cat_h18', title: 'Massage Chair', description: 'Full-body massage chair', price: 1200000, images: [{ url: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p29', shopId: 'shop2', categoryId: 'cat_h22', title: 'Hair Dryer', description: 'Professional hair dryer', price: 85000, images: [{ url: 'https://images.unsplash.com/photo-1522338242992-e1a55dcb0b8f?w=400', sort: 1, isMain: true }], isActive: true },
    { id: 'p30', shopId: 'shop3', categoryId: 'cat_h27', title: 'Air Conditioner', description: 'Energy-efficient AC unit', price: 950000, salePrice: 850000, images: [{ url: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=400', sort: 1, isMain: true }], isActive: true },
  ];
  
  const banners: Banner[] = [
    { id: 'b1', title: 'Summer Sale', imageUrl: 'https://images.unsplash.com/photo-1607082349566-187342175e2f?w=1200', linkType: 'category', linkValue: 'cat1', sort: 1 },
    { id: 'b2', title: 'New Arrivals', imageUrl: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200', linkType: 'category', linkValue: 'cat2', sort: 2 },
    { id: 'b3', title: 'Special Offers', imageUrl: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200', linkType: 'none', sort: 3 },
  ];
  
  setStorage(STORAGE_KEYS.SHOPS, shops);
  setStorage(STORAGE_KEYS.CATEGORIES, categories);
  setStorage(STORAGE_KEYS.PRODUCTS, products);
  setStorage(STORAGE_KEYS.BANNERS, banners);
  setStorage(STORAGE_KEYS.FEATURED_CATEGORIES, [rootElectronics.id, rootBeauty.id, rootHome.id]);
  setItem('nomadshop_seeded', 'true');
}

// Initialize client mock data (must be called from browser only)
export async function initClientMockData(): Promise<void> {
  if (!isBrowser()) return;
  seedData();
}

// API functions
export const mockApi = {
  // Auth
  async login(email: string, otp: string): Promise<User> {
    await delay(800);
    if (otp !== '123456') {
      throw new Error('Invalid OTP');
    }
    const user: User = {
      id: generateId(),
      email,
      role: getStorage<UserRole>(STORAGE_KEYS.ROLE, 'user'),
      name: email.split('@')[0],
      emailVerifiedAt: new Date().toISOString(), // Mock users are auto-verified
    };
    setStorage(STORAGE_KEYS.USER, user);
    return user;
  },
  
  async logout(): Promise<void> {
    await delay(300);
    removeItem(STORAGE_KEYS.USER);
  },
  
  getCurrentUser(): User | null {
    return getStorage<User | null>(STORAGE_KEYS.USER, null);
  },
  
  setRole(role: UserRole): void {
    setStorage(STORAGE_KEYS.ROLE, role);
    const user = this.getCurrentUser();
    if (user) {
      user.role = role;
      setStorage(STORAGE_KEYS.USER, user);
    }
  },
  
  // Password change (placeholder - returns success for now)
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await delay(600);
    // In a real app, this would validate currentPassword and update the password
    // For now, just simulate success
    if (newPassword.length < 8) {
      throw new Error('New password must be at least 8 characters');
    }
    // Password changed successfully (stored in backend in real app)
  },
  
  // Password reset (placeholder - stores token in sessionStorage)
  async requestPasswordReset(email: string): Promise<string> {
    await delay(800);
    // In a real app, this would send an email with a reset link
    // For demo, generate a token and store it
    const token = `DEMO_RESET_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    if (isBrowser()) {
      sessionStorage.setItem('password_reset_token', token);
      sessionStorage.setItem('password_reset_email', email);
    }
    return token;
  },
  
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await delay(800);
    if (newPassword.length < 8) {
      throw new Error('New password must be at least 8 characters');
    }
    // In a real app, this would validate the token and update the password
    if (isBrowser()) {
      const storedToken = sessionStorage.getItem('password_reset_token');
      if (!storedToken || storedToken !== token) {
        throw new Error('Invalid or expired reset token');
      }
      sessionStorage.removeItem('password_reset_token');
      sessionStorage.removeItem('password_reset_email');
    }
    // Password reset successfully (stored in backend in real app)
  },
  
  // Categories
  async getCategories(): Promise<Category[]> {
    await delay(400);
    return getStorage<Category[]>(STORAGE_KEYS.CATEGORIES, []);
  },
  
  async getCategory(id: string): Promise<Category | null> {
    await delay(300);
    const categories = await this.getCategories();
    return categories.find(c => c.id === id) || null;
  },
  
  async createCategory(data: Omit<Category, 'id'>): Promise<Category> {
    await delay(500);
    const category: Category = { ...data, id: generateId() };
    const categories = await this.getCategories();
    categories.push(category);
    setStorage(STORAGE_KEYS.CATEGORIES, categories);
    return category;
  },
  
  async updateCategory(id: string, data: Partial<Category>): Promise<Category> {
    await delay(500);
    const categories = await this.getCategories();
    const index = categories.findIndex(c => c.id === id);
    if (index === -1) throw new Error('Category not found');
    categories[index] = { ...categories[index], ...data };
    setStorage(STORAGE_KEYS.CATEGORIES, categories);
    return categories[index];
  },
  
  async deleteCategory(id: string): Promise<void> {
    await delay(500);
    const categories = await this.getCategories();
    const filtered = categories.filter(c => c.id !== id);
    setStorage(STORAGE_KEYS.CATEGORIES, filtered);
  },
  
  // Products
  async getProducts(filters?: { categoryId?: string; shopId?: string; search?: string }): Promise<Product[]> {
    await delay(500);
    let products = getStorage<Product[]>(STORAGE_KEYS.PRODUCTS, []);
    
    if (filters?.categoryId) {
      const categories = await this.getCategories();
      const targetCategory = categories.find(c => c.id === filters.categoryId);
      
      if (targetCategory) {
        // If it's a root category (no parentId), get all child categories
        if (!targetCategory.parentId) {
          const childCategoryIds = categories
            .filter(c => c.parentId === targetCategory.id)
            .map(c => c.id);
          products = products.filter(p => childCategoryIds.includes(p.categoryId));
        } else {
          // If it's a leaf category, filter by that category only
          products = products.filter(p => p.categoryId === filters.categoryId);
        }
      }
    }
    
    if (filters?.shopId) {
      products = products.filter(p => p.shopId === filters.shopId);
    }
    if (filters?.search) {
      const search = filters.search.toLowerCase();
      products = products.filter(p => 
        p.title.toLowerCase().includes(search) || 
        p.description.toLowerCase().includes(search)
      );
    }
    return products.filter(p => p.isActive);
  },
  
  async getProduct(id: string): Promise<Product | null> {
    await delay(300);
    const products = await this.getProducts();
    return products.find(p => p.id === id) || null;
  },
  
  async createProduct(data: Omit<Product, 'id'>): Promise<Product> {
    await delay(600);
    const product: Product = { ...data, id: generateId() };
    const products = await this.getProducts();
    products.push(product);
    setStorage(STORAGE_KEYS.PRODUCTS, products);
    return product;
  },
  
  async updateProduct(id: string, data: Partial<Product>): Promise<Product> {
    await delay(600);
    const products = getStorage<Product[]>(STORAGE_KEYS.PRODUCTS, []);
    const index = products.findIndex(p => p.id === id);
    if (index === -1) throw new Error('Product not found');
    products[index] = { ...products[index], ...data };
    setStorage(STORAGE_KEYS.PRODUCTS, products);
    return products[index];
  },
  
  async deleteProduct(id: string): Promise<void> {
    await delay(500);
    const products = getStorage<Product[]>(STORAGE_KEYS.PRODUCTS, []);
    const filtered = products.filter(p => p.id !== id);
    setStorage(STORAGE_KEYS.PRODUCTS, filtered);
  },
  
  // Shops
  async getShops(): Promise<Shop[]> {
    await delay(300);
    return getStorage<Shop[]>(STORAGE_KEYS.SHOPS, []);
  },
  
  async getShop(id: string): Promise<Shop | null> {
    await delay(200);
    const shops = await this.getShops();
    return shops.find(s => s.id === id) || null;
  },
  
  async getMyShop(userId: string): Promise<Shop | null> {
    await delay(200);
    const shops = await this.getShops();
    return shops.find(s => s.ownerUserId === userId) || null;
  },
  
  async updateShop(shopId: string, patch: Partial<Shop>): Promise<Shop> {
    await delay(500);
    const shops = await this.getShops();
    const index = shops.findIndex(s => s.id === shopId);
    if (index === -1) throw new Error('Shop not found');
    shops[index] = { ...shops[index], ...patch };
    setStorage(STORAGE_KEYS.SHOPS, shops);
    return shops[index];
  },
  
  // Cart
  async getCart(userId: string): Promise<Cart | null> {
    await delay(200);
    const carts = getStorage<Cart[]>(STORAGE_KEYS.CART, []);
    return carts.find(c => c.userId === userId) || null;
  },
  
  async addToCart(userId: string, productId: string, qty: number): Promise<Cart> {
    await delay(400);
    const product = await this.getProduct(productId);
    if (!product) throw new Error('Product not found');
    
    const carts = getStorage<Cart[]>(STORAGE_KEYS.CART, []);
    let cart = carts.find(c => c.userId === userId);
    
    if (cart && cart.shopId !== product.shopId) {
      throw new Error('DIFFERENT_SHOP');
    }
    
    if (!cart) {
      cart = { userId, shopId: product.shopId, items: [] };
      carts.push(cart);
    }
    
    const existingItem = cart.items.find(item => item.productId === productId);
    if (existingItem) {
      existingItem.qty += qty;
    } else {
      cart.items.push({ productId, qty });
    }
    
    setStorage(STORAGE_KEYS.CART, carts);
    return cart;
  },
  
  async updateCartItem(userId: string, productId: string, qty: number): Promise<Cart> {
    await delay(300);
    const carts = getStorage<Cart[]>(STORAGE_KEYS.CART, []);
    const cart = carts.find(c => c.userId === userId);
    if (!cart) throw new Error('Cart not found');
    
    const item = cart.items.find(i => i.productId === productId);
    if (!item) throw new Error('Item not found');
    
    if (qty <= 0) {
      cart.items = cart.items.filter(i => i.productId !== productId);
    } else {
      item.qty = qty;
    }
    
    setStorage(STORAGE_KEYS.CART, carts);
    return cart;
  },
  
  async removeFromCart(userId: string, productId: string): Promise<Cart> {
    await delay(300);
    const carts = getStorage<Cart[]>(STORAGE_KEYS.CART, []);
    const cart = carts.find(c => c.userId === userId);
    if (!cart) throw new Error('Cart not found');
    
    cart.items = cart.items.filter(i => i.productId !== productId);
    setStorage(STORAGE_KEYS.CART, carts);
    return cart;
  },
  
  async clearCart(userId: string): Promise<void> {
    await delay(300);
    const carts = getStorage<Cart[]>(STORAGE_KEYS.CART, []);
    const filtered = carts.filter(c => c.userId !== userId);
    setStorage(STORAGE_KEYS.CART, filtered);
  },
  
  // Wishlist
  async getWishlist(userId: string): Promise<string[]> {
    await delay(200);
    const wishlists = getStorage<Record<string, string[]>>(STORAGE_KEYS.WISHLIST, {});
    return wishlists[userId] || [];
  },
  
  async addToWishlist(userId: string, productId: string): Promise<void> {
    await delay(300);
    const wishlists = getStorage<Record<string, string[]>>(STORAGE_KEYS.WISHLIST, {});
    if (!wishlists[userId]) wishlists[userId] = [];
    if (!wishlists[userId].includes(productId)) {
      wishlists[userId].push(productId);
    }
    setStorage(STORAGE_KEYS.WISHLIST, wishlists);
  },
  
  async removeFromWishlist(userId: string, productId: string): Promise<void> {
    await delay(300);
    const wishlists = getStorage<Record<string, string[]>>(STORAGE_KEYS.WISHLIST, {});
    if (wishlists[userId]) {
      wishlists[userId] = wishlists[userId].filter(id => id !== productId);
    }
    setStorage(STORAGE_KEYS.WISHLIST, wishlists);
  },
  
  // Orders
  async getOrders(filters?: { userId?: string; shopId?: string; status?: OrderStatus }): Promise<Order[]> {
    await delay(500);
    let orders = getStorage<Order[]>(STORAGE_KEYS.ORDERS, []);
    if (filters?.userId) {
      orders = orders.filter(o => o.userId === filters.userId);
    }
    if (filters?.shopId) {
      orders = orders.filter(o => o.shopId === filters.shopId);
    }
    if (filters?.status) {
      orders = orders.filter(o => o.status === filters.status);
    }
    return orders.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  },
  
  async getOrder(id: string): Promise<Order | null> {
    await delay(300);
    const orders = await this.getOrders();
    return orders.find(o => o.id === id) || null;
  },
  
  async createOrder(userId: string, address: Order['address']): Promise<Order> {
    await delay(800);
    const cart = await this.getCart(userId);
    if (!cart || cart.items.length === 0) {
      throw new Error('Cart is empty');
    }
    
    const products = await this.getProducts();
    const orderItems: OrderItem[] = cart.items.map(item => {
      const product = products.find(p => p.id === item.productId);
      if (!product) throw new Error('Product not found');
      return {
        productId: product.id,
        titleSnapshot: product.title,
        priceSnapshot: product.salePrice || product.price,
        qty: item.qty,
      };
    });
    
    const itemsTotal = orderItems.reduce((sum, item) => sum + item.priceSnapshot * item.qty, 0);
    const shippingFee = 5000;
    const total = itemsTotal + shippingFee;
    
    const order: Order = {
      id: generateId(),
      userId,
      shopId: cart.shopId,
      items: orderItems,
      amounts: { itemsTotal, shippingFee, total },
      status: 'PENDING_PAYMENT',
      statusHistory: [{
        status: 'PENDING_PAYMENT',
        byRole: 'user',
        at: new Date().toISOString(),
      }],
      createdAt: new Date().toISOString(),
      address,
    };
    
    const orders = await this.getOrders();
    orders.push(order);
    setStorage(STORAGE_KEYS.ORDERS, orders);
    
    // Create QPay payment
    const payments = getStorage<QPayPayment[]>(STORAGE_KEYS.QPAY_PAYMENTS, []);
    payments.push({
      id: generateId(),
      orderId: order.id,
      invoiceId: `INV-${order.id}`,
      status: 'PENDING',
      createdAt: new Date().toISOString(),
    });
    setStorage(STORAGE_KEYS.QPAY_PAYMENTS, payments);
    
    // Clear cart
    await this.clearCart(userId);
    
    return order;
  },
  
  async updateOrderStatus(id: string, status: OrderStatus, byRole: UserRole): Promise<Order> {
    await delay(500);
    const orders = getStorage<Order[]>(STORAGE_KEYS.ORDERS, []);
    const order = orders.find(o => o.id === id);
    if (!order) throw new Error('Order not found');
    
    order.status = status;
    order.statusHistory.push({
      status,
      byRole,
      at: new Date().toISOString(),
    });
    
    setStorage(STORAGE_KEYS.ORDERS, orders);
    
    // Update QPay payment if status is PAID
    if (status === 'PAID') {
      const payments = getStorage<QPayPayment[]>(STORAGE_KEYS.QPAY_PAYMENTS, []);
      const payment = payments.find(p => p.orderId === id);
      if (payment) {
        payment.status = 'PAID';
        setStorage(STORAGE_KEYS.QPAY_PAYMENTS, payments);
      }
    }
    
    return order;
  },
  
  // QPay Payments
  async getQPayPayments(): Promise<QPayPayment[]> {
    await delay(400);
    return getStorage<QPayPayment[]>(STORAGE_KEYS.QPAY_PAYMENTS, []);
  },
  
  // Refunds
  async createRefund(orderId: string, type: RefundType, amount: number): Promise<Refund> {
    await delay(600);
    const refund: Refund = {
      id: generateId(),
      orderId,
      type,
      amount,
      status: 'PENDING',
      createdAt: new Date().toISOString(),
    };
    const refunds = getStorage<Refund[]>(STORAGE_KEYS.REFUNDS, []);
    refunds.push(refund);
    setStorage(STORAGE_KEYS.REFUNDS, refunds);
    return refund;
  },
  
  // Banners
  async getBanners(): Promise<Banner[]> {
    await delay(300);
    return getStorage<Banner[]>(STORAGE_KEYS.BANNERS, []).sort((a, b) => a.sort - b.sort);
  },
  
  async createBanner(data: Omit<Banner, 'id'>): Promise<Banner> {
    await delay(500);
    const banner: Banner = { ...data, id: generateId() };
    const banners = await this.getBanners();
    banners.push(banner);
    setStorage(STORAGE_KEYS.BANNERS, banners);
    return banner;
  },
  
  async updateBanner(id: string, data: Partial<Banner>): Promise<Banner> {
    await delay(500);
    const banners = getStorage<Banner[]>(STORAGE_KEYS.BANNERS, []);
    const index = banners.findIndex(b => b.id === id);
    if (index === -1) throw new Error('Banner not found');
    banners[index] = { ...banners[index], ...data };
    setStorage(STORAGE_KEYS.BANNERS, banners);
    return banners[index];
  },
  
  async deleteBanner(id: string): Promise<void> {
    await delay(500);
    const banners = getStorage<Banner[]>(STORAGE_KEYS.BANNERS, []);
    const filtered = banners.filter(b => b.id !== id);
    setStorage(STORAGE_KEYS.BANNERS, filtered);
  },
  
  // Featured Categories
  async getFeaturedCategories(): Promise<string[]> {
    await delay(200);
    return getStorage<string[]>(STORAGE_KEYS.FEATURED_CATEGORIES, []);
  },
  
  async setFeaturedCategories(categoryIds: string[]): Promise<void> {
    await delay(300);
    setStorage(STORAGE_KEYS.FEATURED_CATEGORIES, categoryIds);
  },
  
  // Reset all data
  async resetAllData(): Promise<void> {
    await delay(500);
    Object.values(STORAGE_KEYS).forEach(key => {
      if (key !== STORAGE_KEYS.SELLER_DEMO_META) {
        removeItem(key);
      }
    });
    removeItem('nomadshop_seeded');
    // Clear seller demo metadata when resetting all data
    removeItem(STORAGE_KEYS.SELLER_DEMO_META);
    seedData();
  },
  
  // Seller-scoped demo data seeding
  async seedSellerDemoData(ownerUserId: string): Promise<{ shopId: string }> {
    await delay(1000);
    
    // Get or create shop for this seller
    let shop = await this.getMyShop(ownerUserId);
    let shopId: string;
    
    if (!shop) {
      // Create a demo shop for this seller
      shopId = `seller_shop_${ownerUserId}`;
      shop = {
        id: shopId,
        ownerUserId,
        name: `Shop for ${ownerUserId}`,
        contactEmail: `${ownerUserId}@shop.mn`,
        contactPhone: '99112233',
      };
      const shops = await this.getShops();
      shops.push(shop);
      setStorage(STORAGE_KEYS.SHOPS, shops);
    } else {
      shopId = shop.id;
    }
    
    // Get categories
    const categories = await this.getCategories();
    const leafCategories = categories.filter(c => c.parentId).map(c => c.id);
    if (leafCategories.length === 0) {
      throw new Error('No categories available. Please seed categories first.');
    }
    
    // Create demo products for this shop (8-15 products)
    const existingProducts = getStorage<Product[]>(STORAGE_KEYS.PRODUCTS, []);
    const demoProducts: Product[] = [];
    
    const productTemplates = [
      { title: 'Premium Smartphone', price: 850000, salePrice: 750000, stock: 15 },
      { title: 'Wireless Earbuds Pro', price: 120000, salePrice: 99000, stock: 30 },
      { title: 'Laptop Ultra 15"', price: 1200000, stock: 8 },
      { title: 'Smart Watch Series 5', price: 350000, salePrice: 299000, stock: 20 },
      { title: 'Tablet Air 10"', price: 450000, salePrice: 380000, stock: 12 },
      { title: 'Bluetooth Speaker', price: 85000, salePrice: 65000, stock: 25 },
      { title: 'Skincare Set Premium', price: 125000, salePrice: 99000, stock: 18 },
      { title: 'Makeup Palette Pro', price: 95000, stock: 22 },
      { title: 'Hair Care Kit', price: 75000, stock: 15 },
      { title: 'Perfume Luxury', price: 180000, stock: 10 },
      { title: 'Air Fryer Multi', price: 280000, stock: 14 },
      { title: 'Coffee Maker Auto', price: 320000, salePrice: 280000, stock: 9 },
    ];
    
    const numProducts = Math.min(12, productTemplates.length);
    const timestamp = Date.now();
    for (let i = 0; i < numProducts; i++) {
      const template = productTemplates[i];
      const categoryId = leafCategories[i % leafCategories.length];
      const productId = `seller_demo_p_${ownerUserId}_${timestamp}_${i}`;
      
      demoProducts.push({
        id: productId,
        shopId,
        categoryId,
        title: template.title,
        description: `High-quality ${template.title.toLowerCase()} with excellent features and performance.`,
        price: template.price,
        salePrice: template.salePrice,
        images: [{
          url: `https://images.unsplash.com/photo-${1500000000000 + i}?w=400`,
          sort: 1,
          isMain: true
        }],
        isActive: true,
        stock: template.stock,
      });
    }
    
    const allProducts = [...existingProducts, ...demoProducts];
    setStorage(STORAGE_KEYS.PRODUCTS, allProducts);
    
    // Create demo orders for this shop (5-10 orders)
    const existingOrders = getStorage<Order[]>(STORAGE_KEYS.ORDERS, []);
    const demoOrders: Order[] = [];
    
    const orderStatuses: OrderStatus[] = ['PENDING_PAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'PENDING_PAYMENT', 'PAID'];
    const customerNames = ['Bold Bat', 'Sara Munkh', 'Temuulen Gan', 'Naran Erdene', 'Batbayar Dorj', 'Oyuna Tsetseg', 'Ganbaatar Bold', 'Munkhbat Altai'];
    const cities = ['Ulaanbaatar', 'Darkhan', 'Erdenet', 'Choibalsan'];
    
    const numOrders = 8;
    for (let i = 0; i < numOrders; i++) {
      const status = orderStatuses[i % orderStatuses.length];
      const customerName = customerNames[i % customerNames.length];
      const city = cities[i % cities.length];
      
      // Select 1-3 random products from demo products
      const numItems = Math.floor(Math.random() * 3) + 1;
      const selectedProducts = demoProducts.slice(0, Math.min(numItems, demoProducts.length));
      
      const orderItems: OrderItem[] = selectedProducts.map(p => ({
        productId: p.id,
        titleSnapshot: p.title,
        priceSnapshot: p.salePrice || p.price,
        qty: Math.floor(Math.random() * 3) + 1,
      }));
      
      const itemsTotal = orderItems.reduce((sum, item) => sum + item.priceSnapshot * item.qty, 0);
      const shippingFee = 5000;
      const total = itemsTotal + shippingFee;
      
      const createdAt = new Date();
      createdAt.setDate(createdAt.getDate() - Math.floor(Math.random() * 30));
      
      const statusHistory: StatusHistoryEntry[] = [
        { status: 'PENDING_PAYMENT', byRole: 'user', at: createdAt.toISOString() },
      ];
      
      if (status !== 'PENDING_PAYMENT') {
        statusHistory.push({ status: 'PAID', byRole: 'user', at: new Date(createdAt.getTime() + 3600000).toISOString() });
      }
      if (status === 'SHIPPED' || status === 'DELIVERED') {
        statusHistory.push({ status: 'SHIPPED', byRole: 'seller', at: new Date(createdAt.getTime() + 86400000).toISOString() });
      }
      if (status === 'DELIVERED') {
        statusHistory.push({ status: 'DELIVERED', byRole: 'seller', at: new Date(createdAt.getTime() + 172800000).toISOString() });
      }
      
      const orderId = `seller_demo_order_${ownerUserId}_${timestamp}_${i}`;
      demoOrders.push({
        id: orderId,
        userId: `demo_user_${i + 1}`,
        shopId,
        items: orderItems,
        amounts: { itemsTotal, shippingFee, total },
        status,
        statusHistory,
        createdAt: createdAt.toISOString(),
        address: {
          fullName: customerName,
          phone: `99${100000 + i}`,
          city,
          addressLine1: `Building ${i + 1}, Street ${i + 1}, District ${i + 1}`,
        },
      });
    }
    
    const allOrders = [...existingOrders, ...demoOrders];
    setStorage(STORAGE_KEYS.ORDERS, allOrders);
    
    // Store metadata for reset
    const demoMeta = getStorage<Record<string, { productIds: string[]; orderIds: string[]; shopId?: string }>>(STORAGE_KEYS.SELLER_DEMO_META, {});
    demoMeta[ownerUserId] = {
      productIds: demoProducts.map(p => p.id),
      orderIds: demoOrders.map(o => o.id),
      shopId: shopId,
    };
    setStorage(STORAGE_KEYS.SELLER_DEMO_META, demoMeta);
    
    return { shopId };
  },
  
  async resetSellerDemoData(ownerUserId: string): Promise<void> {
    await delay(500);
    
    // Get metadata for this seller
    const demoMeta = getStorage<Record<string, { productIds: string[]; orderIds: string[]; shopId?: string }>>(STORAGE_KEYS.SELLER_DEMO_META, {});
    const sellerMeta = demoMeta[ownerUserId];
    
    if (!sellerMeta) {
      // No demo data found for this seller
      return;
    }
    
    // Remove demo products
    const products = getStorage<Product[]>(STORAGE_KEYS.PRODUCTS, []);
    const filteredProducts = products.filter(p => !sellerMeta.productIds.includes(p.id));
    setStorage(STORAGE_KEYS.PRODUCTS, filteredProducts);
    
    // Remove demo orders
    const orders = getStorage<Order[]>(STORAGE_KEYS.ORDERS, []);
    const filteredOrders = orders.filter(o => !sellerMeta.orderIds.includes(o.id));
    setStorage(STORAGE_KEYS.ORDERS, filteredOrders);
    
    // Note: We don't delete the shop as it might have been created manually or have other data
    
    // Remove metadata
    delete demoMeta[ownerUserId];
    setStorage(STORAGE_KEYS.SELLER_DEMO_META, demoMeta);
  },
  
  getSellerDemoMeta(ownerUserId: string): { productIds: string[]; orderIds: string[]; shopId?: string } | null {
    try {
      const demoMeta = getStorage<Record<string, { productIds: string[]; orderIds: string[]; shopId?: string }>>(STORAGE_KEYS.SELLER_DEMO_META, {});
      return demoMeta[ownerUserId] || null;
    } catch {
      return null;
    }
  },
  
  async seedDemoData(): Promise<void> {
    await delay(1000);
    
    // Get existing categories or use seed data categories
    let categories = getStorage<Category[]>(STORAGE_KEYS.CATEGORIES, []);
    if (categories.length === 0) {
      seedData();
      categories = getStorage<Category[]>(STORAGE_KEYS.CATEGORIES, []);
    }
    
    // Get leaf categories for product distribution
    const leafCategories = categories.filter(c => c.parentId).map(c => c.id);
    if (leafCategories.length === 0) {
      throw new Error('No categories available. Please seed categories first.');
    }
    
    // Create demo shops (1-2 shops)
    const existingShops = getStorage<Shop[]>(STORAGE_KEYS.SHOPS, []);
    const demoShops: Shop[] = [];
    
    if (existingShops.length === 0) {
      demoShops.push(
        { id: 'demo_shop1', ownerUserId: 'demo_seller1', name: 'Mongolian Crafts', contactEmail: 'crafts@demo.mn', contactPhone: '99112233' },
        { id: 'demo_shop2', ownerUserId: 'demo_seller2', name: 'Nomad Essentials', contactEmail: 'essentials@demo.mn', contactPhone: '99223344' }
      );
      setStorage(STORAGE_KEYS.SHOPS, demoShops);
    } else {
      demoShops.push(...existingShops.slice(0, 2));
    }
    
    const shopIds = demoShops.map(s => s.id);
    
    // Create demo products (8-15 products)
    const existingProducts = getStorage<Product[]>(STORAGE_KEYS.PRODUCTS, []);
    const demoProducts: Product[] = [];
    
    const productTemplates = [
      { title: 'Premium Smartphone', price: 850000, salePrice: 750000, stock: 15 },
      { title: 'Wireless Earbuds Pro', price: 120000, salePrice: 99000, stock: 30 },
      { title: 'Laptop Ultra 15"', price: 1200000, stock: 8 },
      { title: 'Smart Watch Series 5', price: 350000, salePrice: 299000, stock: 20 },
      { title: 'Tablet Air 10"', price: 450000, salePrice: 380000, stock: 12 },
      { title: 'Bluetooth Speaker', price: 85000, salePrice: 65000, stock: 25 },
      { title: 'Skincare Set Premium', price: 125000, salePrice: 99000, stock: 18 },
      { title: 'Makeup Palette Pro', price: 95000, stock: 22 },
      { title: 'Hair Care Kit', price: 75000, stock: 15 },
      { title: 'Perfume Luxury', price: 180000, stock: 10 },
      { title: 'Air Fryer Multi', price: 280000, stock: 14 },
      { title: 'Coffee Maker Auto', price: 320000, salePrice: 280000, stock: 9 },
      { title: 'Vacuum Cleaner Pro', price: 450000, salePrice: 380000, stock: 7 },
      { title: 'Air Purifier HEPA', price: 550000, stock: 6 },
      { title: 'Massage Chair Full', price: 1200000, stock: 3 },
    ];
    
    let productIdCounter = existingProducts.length + 1;
    for (let i = 0; i < Math.min(15, productTemplates.length); i++) {
      const template = productTemplates[i];
      const categoryId = leafCategories[i % leafCategories.length];
      const shopId = shopIds[i % shopIds.length];
      
      demoProducts.push({
        id: `demo_p${productIdCounter++}`,
        shopId,
        categoryId,
        title: template.title,
        description: `High-quality ${template.title.toLowerCase()} with excellent features and performance.`,
        price: template.price,
        salePrice: template.salePrice,
        images: [{
          url: `https://images.unsplash.com/photo-${1500000000000 + i}?w=400`,
          sort: 1,
          isMain: true
        }],
        isActive: true,
        stock: template.stock,
      });
    }
    
    const allProducts = [...existingProducts, ...demoProducts];
    setStorage(STORAGE_KEYS.PRODUCTS, allProducts);
    
    // Create demo orders (5-10 orders)
    const existingOrders = getStorage<Order[]>(STORAGE_KEYS.ORDERS, []);
    const demoOrders: Order[] = [];
    
    const orderStatuses: OrderStatus[] = ['PENDING_PAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'PENDING_PAYMENT', 'PAID'];
    const customerNames = ['Bold Bat', 'Sara Munkh', 'Temuulen Gan', 'Naran Erdene', 'Batbayar Dorj', 'Oyuna Tsetseg', 'Ganbaatar Bold', 'Munkhbat Altai'];
    const cities = ['Ulaanbaatar', 'Darkhan', 'Erdenet', 'Choibalsan'];
    
    let orderIdCounter = existingOrders.length + 1;
    for (let i = 0; i < 8; i++) {
      const status = orderStatuses[i % orderStatuses.length];
      const customerName = customerNames[i % customerNames.length];
      const city = cities[i % cities.length];
      const shopId = shopIds[i % shopIds.length];
      
      // Select 1-3 random products from demo products
      const numItems = Math.floor(Math.random() * 3) + 1;
      const selectedProducts = demoProducts.slice(0, Math.min(numItems, demoProducts.length));
      
      const orderItems: OrderItem[] = selectedProducts.map(p => ({
        productId: p.id,
        titleSnapshot: p.title,
        priceSnapshot: p.salePrice || p.price,
        qty: Math.floor(Math.random() * 3) + 1,
      }));
      
      const itemsTotal = orderItems.reduce((sum, item) => sum + item.priceSnapshot * item.qty, 0);
      const shippingFee = 5000;
      const total = itemsTotal + shippingFee;
      
      const createdAt = new Date();
      createdAt.setDate(createdAt.getDate() - Math.floor(Math.random() * 30));
      
      const statusHistory: StatusHistoryEntry[] = [
        { status: 'PENDING_PAYMENT', byRole: 'user', at: createdAt.toISOString() },
      ];
      
      if (status !== 'PENDING_PAYMENT') {
        statusHistory.push({ status: 'PAID', byRole: 'user', at: new Date(createdAt.getTime() + 3600000).toISOString() });
      }
      if (status === 'SHIPPED' || status === 'DELIVERED') {
        statusHistory.push({ status: 'SHIPPED', byRole: 'seller', at: new Date(createdAt.getTime() + 86400000).toISOString() });
      }
      if (status === 'DELIVERED') {
        statusHistory.push({ status: 'DELIVERED', byRole: 'seller', at: new Date(createdAt.getTime() + 172800000).toISOString() });
      }
      
      demoOrders.push({
        id: `demo_order${orderIdCounter++}`,
        userId: `demo_user${i + 1}`,
        shopId,
        items: orderItems,
        amounts: { itemsTotal, shippingFee, total },
        status,
        statusHistory,
        createdAt: createdAt.toISOString(),
        address: {
          fullName: customerName,
          phone: `99${100000 + i}`,
          city,
          addressLine1: `Building ${i + 1}, Street ${i + 1}, District ${i + 1}`,
        },
      });
    }
    
    const allOrders = [...existingOrders, ...demoOrders];
    setStorage(STORAGE_KEYS.ORDERS, allOrders);
    
    // Mark as seeded
    setItem('nomadshop_seeded', 'true');
  },
};

